import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { UserActivationPayload } from '@shared/models/activation.models';
import { MemberProfile } from '@shared/models/member-profile';
import { SecurityQuestions } from '@shared/models/security-questions';
import { SetNewPasswordPayload } from '@shared/models/set-new-password-payload';
import { SiteSettingsService } from '@shared/services/site-settings.service';
import { ApiEndpoint } from '@shared/shared/enums/api-endpoints.enum';
import { EligibilitiesAndSelfRegEnum } from '@shared/shared/enums/eligibilities-and-selfregs.enum';
import { replaceTemplateKeys } from '@shared/utils/url.lib';
import { Observable, of, throwError } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { ChallengeQuestionPayload } from '../models/challenge-question-payload';
import { Eligibility } from '../models/eligibility';
import { EligibilityRequest } from '../models/eligibility-request';
import { MembershipReferral } from '../models/membership-referral';
import { UserDetails } from '../models/user-details';
import { UserEligibleResponseExternal } from '../models/user-eligible-response-external';
import { DataLayerUtilsService } from './data-layer-utils.service';

export enum LocalStorageKey {
  Res = 'eligibility-check-res',
}

@Injectable({
  providedIn: 'root',
})
export class DataLayerService {
  private brandPath = '';
  public eligibilityType: string;

  private _eligibilityTokenUUID: string;
  public get eligibilityTokenUUID(): string {
    return this._eligibilityTokenUUID;
  }
  public set eligibilityTokenUUID(value: string) {
    this._eligibilityTokenUUID = value;
  }

  public eligibilityRequest: EligibilityRequest;

  private _eligibilityResponse: any;
  public get eligibilityResponse(): any {
    if (!this._eligibilityResponse) {
      let data;
      try {
        data = localStorage.getItem(LocalStorageKey.Res);
      } catch (err) {
        console.warn('Could not use local storage for eligibility', err);
      }
      this._eligibilityResponse = JSON.parse(data);
    }
    return this._eligibilityResponse;
  }
  public set eligibilityResponse(value: any) {
    this._eligibilityResponse = value;
    try {
      localStorage.setItem(LocalStorageKey.Res, JSON.stringify(value));
    } catch (err) {
      console.warn('Could not use local storage for eligibility', err);
    }
  }

  constructor(
    private http: HttpClient,
    siteSettingsService: SiteSettingsService,
    private router: Router
  ) {
    siteSettingsService.fetchSiteSettings().subscribe((res) => {
      this.brandPath = res.fe_configuration.path;
      this.eligibilityType = res.eligibilities_and_selfregs;
    });
  }

  // used for Eligibility when bypassing site selector
  public hasSiteId(): boolean {
    return (
      new URLSearchParams(window.location.search).get('site_id') !== null &&
      new URLSearchParams(window.location.search).get('site_id') !== undefined
    );
  }

  // method used in router guards to determine if necessary data has been collected
  public hasEligibilityCachedData(): boolean {
    return this.hasSiteId() || !!this.eligibilityResponse;
  }

  public checkEligibility(
    eligibilityRequest: EligibilityRequest,
    eligibilityType?: EligibilitiesAndSelfRegEnum
  ): Observable<Eligibility> {
    this.eligibilityRequest = eligibilityRequest;
    switch (eligibilityType) {
      case EligibilitiesAndSelfRegEnum.TM_LINK_REF_1: {
        return this.http
          .post(ApiEndpoint.REF1_CHECK_ELIGIBILITY, {
            'type': eligibilityRequest.identifier,
            'identifier': eligibilityRequest.value,
            'g-recaptcha-response': eligibilityRequest.captchaToken,
          })
          .pipe(
            mergeMap((response: any) => {
              this.eligibilityTokenUUID = response.eligibility_token_uuid;
              this.eligibilityResponse = {
                token: response.token,
              };
              return of({ isEligible: true, token: response.eligibility_token_uuid });
            }),
            catchError((error: HttpErrorResponse) => throwError(DataLayerUtilsService.extractValidationErrors(error)))
          );
      }
      case EligibilitiesAndSelfRegEnum.TM_LINK_REF_1_REF_2: {
        return this.http
          .post(ApiEndpoint.DBSAMEX_CHECK_ELIGIBILITY, {
            'eligibility_identifier': eligibilityRequest.identifier,
            'eligibility_second_identifier': eligibilityRequest.identifier2,
            'g-recaptcha-response': eligibilityRequest.captchaToken,
          })
          .pipe(
            mergeMap((response: any) => {
              this.eligibilityTokenUUID = response.eligibility_token_uuid;
              this.eligibilityResponse = {
                token: response.token,
              };
              return of({ isEligible: true, token: response.eligibility_token_uuid });
            }),
            catchError((error: HttpErrorResponse) => throwError(DataLayerUtilsService.extractValidationErrors(error)))
          );
      }
      case EligibilitiesAndSelfRegEnum.NON_TM_LINK: {
        return this.http
          .post(ApiEndpoint.ELIGIBILITY_CHECK, {
            'eligibility_identifier': eligibilityRequest.identifier,
            'site_product_identifier': eligibilityRequest.identifier2,
            'g-recaptcha-response': eligibilityRequest.captchaToken,
          })
          .pipe(
            mergeMap((response: any) => {
              this.eligibilityTokenUUID = response.eligibility_token_uuid;
              this.eligibilityResponse = {
                token: response.token,
              };
              return of({ isEligible: true, token: response.eligibility_token_uuid });
            }),
            catchError((error: HttpErrorResponse) => throwError(DataLayerUtilsService.extractValidationErrors(error)))
          );
      }
      case EligibilitiesAndSelfRegEnum.EXTERNAL: {
        return this.http
          .post(ApiEndpoint.EXTERNAL_OR_CLIENT_VERIFIED_ELIGIBILITY, {
            [eligibilityRequest.identifier]: eligibilityRequest.value,
            'g-recaptcha-response': eligibilityRequest.captchaToken,
          })
          .pipe(
            mergeMap((response: UserEligibleResponseExternal) => {
              this.eligibilityTokenUUID = response.eligibility_token_uuid;
              this.eligibilityResponse = DataLayerUtilsService.extractClientData(response);
              return of({ isEligible: true, siteId: response.site_id, token: response.eligibility_token_uuid });
            }),
            catchError((error: HttpErrorResponse) => {
              const validationErrors = DataLayerUtilsService.extractValidationErrors(error);
              return of({ isEligible: false, errors: validationErrors });
            })
          );
      }
      default:
        console.warn(`Unexpected eligibilityType "${eligibilityType}"`);
        break;
    }
  }

  /**
   * Clears the local storage for the eligibility data
   */
  public clearLocalStorage() {
    try {
      localStorage.removeItem(LocalStorageKey.Res);
      localStorage.removeItem('uuidToken');
    } catch (err) {
      console.warn('Could not use local storage for eligibility', err);
    }
  }

  public submitMembershipReferral(
    membershipReferral: MembershipReferral,
    reCaptchaToken?: string
  ): Observable<MemberProfile> {
    if (this.brandPath === 'centurion') {
      return this.http
        .post(ApiEndpoint.CENTURION_REFERRAL, {
          eligibility_token_uuid: this.eligibilityTokenUUID,
          ...DataLayerUtilsService.mapUserDetailsToCenturionReferralRequest(membershipReferral, reCaptchaToken),
        })
        .pipe(
          mergeMap(() =>
            this.registerCallback({
              firstName: membershipReferral.firstName,
              lastName: membershipReferral.lastName,
              email: membershipReferral.email,
            })
          ),
          catchError((error: HttpErrorResponse) => throwError(DataLayerUtilsService.extractValidationErrors(error)))
        );
    }
  }

  public register(userDetails: UserDetails, reCaptchaToken?: string): Observable<MemberProfile> {
    switch (this.brandPath) {
      case 'itau':
      case 'mastercard-neo':
      case 'mastercard-rak':
      case 'mastercard-hnw':
      case 'Mastercard_Citi':
      case 'mastercard':
      case 'snben':
      case 'snbar':
      case 'neo': {
        if (this.eligibilityType === EligibilitiesAndSelfRegEnum.NON_TM_LINK) {
          return this.http
            .post(ApiEndpoint.GENERIC_SELF_REGISTER, {
              eligibility_token_uuid: this.eligibilityTokenUUID || localStorage.getItem('uuidToken'),
              ...DataLayerUtilsService.mapUserDetailsToMCRegisterRequest(
                userDetails,
                this.eligibilityResponse,
                reCaptchaToken
              ),
            })
            .pipe(
              mergeMap((response: any) => this.registerCallback(userDetails, response)),
              catchError((error: HttpErrorResponse) => throwError(DataLayerUtilsService.extractValidationErrors(error)))
            );
        } else {
          return this.http
            .post(ApiEndpoint.MASTERCARD_SELF_REGISTER, {
              eligibility_token_uuid: this.eligibilityTokenUUID || localStorage.getItem('uuidToken'),
              ...DataLayerUtilsService.mapUserDetailsToMCRegisterRequest(
                userDetails,
                this.eligibilityResponse,
                reCaptchaToken
              ),
            })
            .pipe(
              mergeMap(() => this.registerCallback(userDetails)),
              catchError((error: HttpErrorResponse) => throwError(DataLayerUtilsService.extractValidationErrors(error)))
            );
        }
      }
      case 'visa-lac':
      case 'visa-cemea':
      case 'visa-us': {
        // If there is a OAuth token in the request data then the user must be doing an OAuth registration
        const requestBody = userDetails.stateToken
          ? DataLayerUtilsService.mapUserDataToVisaOAuthRegisterRequest(
              userDetails,
              this.eligibilityResponse,
              reCaptchaToken
            )
          : DataLayerUtilsService.mapUserDetailsToVisaRegisterRequest(
              userDetails,
              this.eligibilityResponse,
              reCaptchaToken
            );

        return this.http
          .post(ApiEndpoint.VISA_SELF_REGISTER, {
            eligibility_token_uuid: this.eligibilityTokenUUID || localStorage.getItem('uuidToken'),
            ...requestBody,
          })
          .pipe(
            mergeMap(() => this.registerCallback(userDetails)),
            catchError((error: HttpErrorResponse) => throwError(DataLayerUtilsService.extractValidationErrors(error)))
          );
      }
      case 'hsbc-jade':
      case 'hsbc-concierge':
      case 'standard-chartered': {
        return this.http
          .post(ApiEndpoint.REF1_SELF_REGISTER, {
            eligibility_token_uuid: this.eligibilityTokenUUID || localStorage.getItem('uuidToken'),
            ...DataLayerUtilsService.mapUserDetailsToHsbcJadeAndDBSRegisterRequest(
              userDetails,
              this.eligibilityResponse,
              reCaptchaToken
            ),
          })
          .pipe(
            mergeMap(() => this.registerCallback(userDetails)),
            catchError((error: HttpErrorResponse) => throwError(DataLayerUtilsService.extractValidationErrors(error)))
          );
      }
      case 'dbsamex': {
        return this.http
          .post(ApiEndpoint.DBSAMEX_SELF_REGISTER, {
            eligibility_token_uuid: this.eligibilityTokenUUID || localStorage.getItem('uuidToken'),
            ...DataLayerUtilsService.mapUserDetailsToHsbcJadeAndDBSRegisterRequest(
              userDetails,
              this.eligibilityResponse,
              reCaptchaToken
            ),
          })
          .pipe(
            mergeMap(() => this.registerCallback(userDetails)),
            catchError((error: HttpErrorResponse) => throwError(DataLayerUtilsService.extractValidationErrors(error)))
          );
      }
      case 'westpac':
      case 'stgeorge':
      case 'bankofmelbourne':
      case 'banksa':
      case 'discover':
      case 'dillards':
      case 'creditone':
      case 'navy-federal':
      case 'wells-fargo':
      case 'creditsaisondiamond':
      case 'absa':
      case 'dnb-saga': {
        return this.http
          .post(ApiEndpoint.GENERIC_SELF_REGISTER, {
            eligibility_token_uuid: this.eligibilityTokenUUID || localStorage.getItem('uuidToken'),
            ...DataLayerUtilsService.mapUserDetailsToGenericRegisterRequest(userDetails, reCaptchaToken),
          })
          .pipe(
            mergeMap((response: any) => this.registerCallback(userDetails, response)),
            catchError((error: HttpErrorResponse) => throwError(DataLayerUtilsService.extractValidationErrors(error)))
          );
      }
      case 'centurion':
        return this.http
          .post(ApiEndpoint.CENTURION_SELF_REGISTER, {
            eligibility_token_uuid: this.eligibilityTokenUUID || localStorage.getItem('uuidToken'),
            ...DataLayerUtilsService.mapUserDetailsToCenturionRegisterRequest(userDetails, reCaptchaToken),
          })
          .pipe(
            mergeMap(() => this.registerCallback(userDetails)),
            catchError((error: HttpErrorResponse) => throwError(DataLayerUtilsService.extractValidationErrors(error)))
          );
      default: {
        return this.http
          .post(ApiEndpoint.REGISTRATION, {
            eligibility_token_uuid: this.eligibilityTokenUUID || localStorage.getItem('uuidToken'),
            ...DataLayerUtilsService.mapUserDetailsToRegisterRequest(userDetails, reCaptchaToken),
          })
          .pipe(
            mergeMap((response: any) => this.registerCallback({ email: response.email } as Partial<UserDetails>)),
            catchError((error: HttpErrorResponse) => throwError(DataLayerUtilsService.extractValidationErrors(error)))
          );
      }
    }
  }

  private registerCallback(userDetails: UserDetails | Partial<UserDetails>, response: any = undefined) {
    this.clearLocalStorage();

    let obj = {
      response,
      email: userDetails.email,
      userName: userDetails.firstName,
      userLastName: userDetails.lastName,
    };
    obj = JSON.parse(JSON.stringify(obj));

    if (Object.keys(obj).length) return of(obj);

    return of({ email: undefined } as MemberProfile);
  }

  public setNewPassword(payload: SetNewPasswordPayload) {
    const { id } = payload;
    const url = replaceTemplateKeys(ApiEndpoint.SET_NEW_PASSWORD, { id });

    return this.http
      .post(url, {
        'activation_token': payload.token,
        'password': payload.password,
        'answer': payload.answer,
        'g-recaptcha-response': payload.recaptcha,
      })
      .pipe(
        mergeMap((response: any) => {
          return of(response);
        }),
        catchError((error: HttpErrorResponse) => throwError(error))
      );
  }

  public newMemberRegistration(payload: any) {
    return this.http
      .post(ApiEndpoint.NEW_MEMBER_REGISTRATION, {
        'first_name': payload.first_name,
        'last_name': payload.last_name,
        'email': payload.email,
        'phone_number': payload.phone_number,
        'g-recaptcha-response': payload['g-recaptcha-response'],
        'already_member': payload['is_already_member'],
      })
      .pipe(
        mergeMap((response: any) => {
          return of(response);
        }),
        catchError((error: HttpErrorResponse) => throwError(DataLayerUtilsService.extractValidationErrors(error)))
      );
  }

  public activateAccount(payload: UserActivationPayload) {
    const { id } = payload;
    const url = replaceTemplateKeys(ApiEndpoint.ACTIVATE_ACCOUNT, { id });

    let body = {
      activation_token: payload.token,
      client_opt_in: payload.optIns && payload.optIns['clientOptIn'] ? payload.optIns['clientOptIn'].value : undefined,
      ten_opt_in: payload.optIns && payload.optIns['tenOptIn'] ? payload.optIns['tenOptIn'].value : undefined,
      terms_and_conditions: true,
    };

    if (!payload.isOAuth) {
      body = <any>{
        ...body,
        password: payload.password,
        security_answers: payload.securityAnswers?.length > 0 ? payload.securityAnswers : undefined,
      };
    }

    return this.http.post(url, body).pipe(
      mergeMap((response: any) => {
        return of(response);
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 404) {
          this.router.navigate(['/error/not-found']);
        } else {
          return throwError(DataLayerUtilsService.extractValidationErrors(error));
        }
      })
    );
  }

  /**
   * Get security questions for user to submit answers to store in their account
   * @returns {Observable<SecurityQuestions>}
   */
  public getSecurityQuestions(): Observable<SecurityQuestions> {
    return this.http.get(ApiEndpoint.REGISTRATION_SECURITY_QUESTIONS).pipe(
      mergeMap((response: any) => {
        return of({
          questionCount: response.question_count,
          questions: response.questions,
        });
      }),
      catchError((error: HttpErrorResponse) => {
        return throwError(DataLayerUtilsService.extractValidationErrors(error));
      })
    );
  }

  /**
   * Get security question for user to answer to validate their identity
   */
  public getChallengeQuestion(payload: ChallengeQuestionPayload): Observable<string> {
    const id = payload.userId;
    const url = replaceTemplateKeys(ApiEndpoint.CHALLENGE_QUESTION, { id });

    return this.http.get(url, { params: new HttpParams().set('token', payload.token) }).pipe(
      mergeMap((response: any) => {
        return of(response.question);
      }),
      catchError((error: HttpErrorResponse) => {
        return throwError(DataLayerUtilsService.extractValidationErrors(error));
      })
    );
  }
}
